import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import './ViewStatsPage.scss';

interface StatsPageViewProps {}

interface LocationState {
    statsId: string;
    statsType: string;
    statsTopic: string;
    statsData: any;
    fromAdmin: boolean;
    navigationTimestamp: number;
}

const StatsPageView: React.FC<StatsPageViewProps> = () => {
    const { statsId } = useParams<{ statsId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statsData, setStatsData] = useState<any>(null);
    const [currentVersionId, setCurrentVersionId] = useState<number | null>(null);
    const [switchingVersion, setSwitchingVersion] = useState(false);
    const [iframeHeight, setIframeHeight] = useState(400);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const resizeTimeoutRef = useRef<NodeJS.Timeout>();

    const serverUrl = process.env.REACT_APP_DRF_DOMAIN || 'http://localhost:8000';
    const state = location.state as LocationState;
    const [copiedButton, setCopiedButton] = useState<string | null>(null);

    // Copy to clipboard function
    const copyToClipboard = async (text: string, buttonId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedButton(buttonId);
            setTimeout(() => setCopiedButton(null), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopiedButton(buttonId);
            setTimeout(() => setCopiedButton(null), 2000);
        }
    };

    useEffect(() => {
        if (state && state.statsData) {
            setStatsData(state.statsData);
            setCurrentVersionId(state.statsData.current_version_id || null);
            setLoading(false);
        } else if (statsId) {
            fetchStatsPageData(statsId);
        } else {
            setError('No stats ID provided');
            setLoading(false);
        }
    }, [statsId, state]);

    const fetchStatsPageData = async (id: string) => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_DRF_DOMAIN}/api/admin/get_ai_stats_page_data/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ stats_id: id })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.status === 'success') {
                setStatsData(data.stats_data);
                setCurrentVersionId(data.current_version_id || null);
            } else {
                throw new Error(data.message || 'Failed to fetch stats data');
            }
        } catch (err) {
            console.error('Error fetching stats data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch stats data');
        } finally {
            setLoading(false);
        }
    };

    const switchToVersionAdmin = async (versionId: number) => {
        if (!statsData) return;

        const selectedVersion = statsData.versions?.find((v: any) => v.id === versionId);
        if (!selectedVersion) return;

        try {
            setSwitchingVersion(true);
            
            const updatedStatsData = {
                ...statsData,
                versions: statsData.versions.map((version: any) => ({
                    ...version,
                    isDisplayed: version.id === versionId
                }))
            };

            setStatsData(updatedStatsData);
            setCurrentVersionId(versionId);
            // Reset iframe height when switching versions
            setIframeHeight(400);
        } catch (error) {
            console.error('Failed to switch version:', error);
            setError('Failed to switch version');
        } finally {
            setSwitchingVersion(false);
        }
    };

    const getDisplayHTML = () => {
        if (!statsData?.versions?.length) return '';
        
        const displayedVersion = statsData.versions.find((v: any) => v.isDisplayed) || 
                                statsData.versions.find((v: any) => v.id === currentVersionId) ||
                                statsData.versions[0];
        
        return displayedVersion?.html_code || '';
    };

    // Improved iframe document creation with better CSS isolation
    const createIframeDocument = (html: string) => {
        // Clean and isolate the HTML content
        const cleanHtml = html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, css) => {
            // Clean CSS and add proper scoping
            const cleanCSS = css
                .replace(/!important/g, '') // Remove existing !important
                .replace(/body\s*{/g, '.iframe-content {') // Scope body styles
                .replace(/html\s*{/g, '.iframe-content {'); // Scope html styles
            
            return `<style>
                .iframe-content {
                    ${cleanCSS}
                }
            </style>`;
        });

        return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Stats Content</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <style>
        /* Complete reset and isolation */
        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
        }
        
        html, body {
            width: 100%;
            height: auto;
            margin: 0;
            padding: 0;
            background: white;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            color: #333;
            overflow-x: hidden;
            overflow-y: auto;
        }
        
        .iframe-content {
            padding: 20px;
            max-width: 100%;
            overflow-wrap: break-word;
            word-wrap: break-word;
        }
        
        /* Responsive elements */
        canvas {
            max-width: 100% !important;
            height: auto !important;
        }
        
        img {
            max-width: 100%;
            height: auto;
        }
        
        table {
            width: 100%;
            table-layout: fixed;
            word-wrap: break-word;
        }
        
        /* Chart containers */
        .chart-container, .chart-wrapper {
            margin: 20px 0;
            padding: 15px;
            background: #f9f9f9;
            border-radius: 8px;
            max-width: 100%;
            overflow: hidden;
        }
        
        /* Typography */
        h1, h2, h3, h4, h5, h6 {
            margin: 1em 0 0.5em 0;
            font-weight: 600;
            line-height: 1.3;
        }
        
        p {
            margin: 0.5em 0;
            line-height: 1.6;
        }
    </style>
</head>
<body>
    <div class="iframe-content">
        ${cleanHtml}
    </div>
    <script>
        let lastHeight = 0;
        let resizeTimeout;
        
        function calculateHeight() {
            const content = document.querySelector('.iframe-content');
            const body = document.body;
            const html = document.documentElement;
            
            const height = Math.max(
                content ? content.scrollHeight : 0,
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight
            );
            
            return Math.max(height, 400); // Minimum 400px
        }
        
        function notifyResize() {
            const newHeight = calculateHeight();
            
            // Only notify if height changed significantly (>10px difference)
            if (Math.abs(newHeight - lastHeight) > 10) {
                lastHeight = newHeight;
                
                if (window.parent && window.parent !== window) {
                    window.parent.postMessage({
                        type: 'iframe-resize',
                        height: newHeight
                    }, '*');
                }
            }
        }
        
        function scheduleResize() {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(notifyResize, 100);
        }
        
        // Initial resize
        setTimeout(notifyResize, 100);
        
        // Chart.js integration
        if (typeof Chart !== 'undefined') {
            const originalChart = Chart;
            
            Chart = function(...args) {
                const chart = new originalChart(...args);
                setTimeout(scheduleResize, 200);
                return chart;
            };
            
            Object.setPrototypeOf(Chart, originalChart);
            Object.assign(Chart, originalChart);
        }
        
        // Observe DOM changes
        const observer = new MutationObserver(scheduleResize);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        });
        
        // Handle window resize
        window.addEventListener('resize', scheduleResize);
        
        // Handle image loads
        document.querySelectorAll('img').forEach(img => {
            if (!img.complete) {
                img.addEventListener('load', scheduleResize);
                img.addEventListener('error', scheduleResize);
            }
        });
        
        // Final resize after everything loads
        window.addEventListener('load', () => {
            setTimeout(notifyResize, 500);
        });
    </script>
</body>
</html>`;
    };

    // Update iframe content with proper cleanup
    const updateIframeContent = (html: string) => {
        if (iframeRef.current) {
            const iframe = iframeRef.current;
            
            // Clear existing timeout
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            
            // Reset height before updating content
            setIframeHeight(400);
            
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (doc) {
                doc.open();
                doc.write(createIframeDocument(html));
                doc.close();
            }
        }
    };

    useEffect(() => {
        if (statsData) {
            const html = getDisplayHTML();
            if (html) {
                updateIframeContent(html);
            }
        }
    }, [statsData, currentVersionId]);

    // Handle iframe resize messages with debouncing
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) return;
            
            if (event.data.type === 'iframe-resize') {
                // Clear existing timeout
                if (resizeTimeoutRef.current) {
                    clearTimeout(resizeTimeoutRef.current);
                }
                
                // Debounce resize updates
                resizeTimeoutRef.current = setTimeout(() => {
                    const newHeight = Math.max(event.data.height, 400);
                    const currentHeight = iframeHeight;
                    
                    // Only update if height changed significantly
                    if (Math.abs(newHeight - currentHeight) > 10) {
                        setIframeHeight(newHeight);
                    }
                }, 150);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [iframeHeight]);

    if (loading) {
        return (
            <div className="stats-page-container">
                <div className="stats-page-loading">
                    <div className="stats-loading-spinner">Loading stats page...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="stats-page-container">
                <div className="stats-error-notification">
                    <button className="stats-error-close" onClick={() => navigate(-1)}>×</button>
                    <strong>Error:</strong> {error}
                </div>
                <button className="stats-back-button" onClick={() => navigate(-1)}>
                    Go Back
                </button>
            </div>
        );
    }

    if (!statsData) {
        return (
            <div className="stats-page-container">
                <div className="stats-warning-notification">
                    No stats data available
                </div>
                <button className="stats-back-button" onClick={() => navigate(-1)}>
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="stats-page-container">
            {/* Admin Header */}
            <div className="stats-admin-card">
                <div className="stats-admin-header">
                    <div className="stats-admin-header-left">
                        <svg 
                            className="back-btn"
                            width="30"
                            height="24"
                            viewBox="0 0 30 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={() => navigate(-1)}
                        >
                            <path d="M26.0435 12.0003H2.82031M2.82031 12.0003L12.8382 1.98242M2.82031 12.0003L12.8382 22.0181" stroke="black" strokeOpacity="0.5" strokeWidth="3" />
                        </svg>
                        <p className="stats-admin-title">
                            Admin View - Stats Page
                        </p>
                    </div>
                </div>
                <div className="stats-admin-content">
                    <div className="stats-admin-row">
                        <div className="stats-admin-col">
                            <strong>Stats Topic:</strong> {statsData.stats_topic}
                        </div>
                        <div className="stats-admin-col">
                            <strong>Stats Type:</strong> {statsData.stats_type}
                        </div>
                        <div className="stats-admin-col">
                            <strong>Stats ID:</strong> {statsData.stats_id}
                        </div>
                    </div>
                    {statsData.admin_info && (
                        <div className="stats-admin-row">
                            <div className="stats-admin-col">
                                <strong>User:</strong> {statsData.admin_info.user_email}
                            </div>
                            <div className="stats-admin-col">
                                <strong>Website:</strong> {statsData.admin_info.website_domain}
                            </div>
                            <div className="stats-admin-col">
                                <strong>Plan:</strong> {statsData.admin_info.user_plan}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Stats Page Content */}
            <div className="stats-content-card">
                <div className="stats-content-header">
                    <p className="stats-content-title">
                        Stats Page Content
                        {currentVersionId && (
                            <span className="stats-version-indicator">
                                (Viewing Version: {statsData.versions?.find((v: any) => v.id === currentVersionId)?.version_name || 'Unknown'})
                            </span>
                        )}
                    </p>
                </div>
                <div className="stats-content-body">
                    {statsData.versions && statsData.versions.length > 0 ? (
                        <div className="stats-content-isolation" style={{ 
                            width: '100%', 
                            overflow: 'hidden',
                            border: '1px solid #e1e5e9',
                            borderRadius: '8px',
                            backgroundColor: 'white'
                        }}>
                            <iframe
                                ref={iframeRef}
                                style={{
                                    width: '100%',
                                    height: `${iframeHeight}px`,
                                    minHeight: '400px',
                                    border: 'none',
                                    display: 'block',
                                    backgroundColor: 'white',
                                    transition: 'height 0.3s ease-out'
                                }}
                                title="Stats Content"
                                sandbox="allow-scripts allow-same-origin"
                                scrolling="no"
                                frameBorder="0"
                            />
                        </div>
                    ) : (
                        <div className="stats-no-content">
                            No content available for this stats page
                        </div>
                    )}
                </div>
            </div>

            {/* Version History */}
            {statsData.versions && statsData.versions.length > 1 && (
                <div className="stats-version-card">
                    <div className="stats-version-header">
                        <p className="stats-version-title">
                            Version History ({statsData.versions.length} versions)
                        </p>
                        <p className="stats-version-subtitle">
                            Click on a version to preview it (admin preview only)
                        </p>
                    </div>
                    <div className="stats-version-content">
                        {statsData.versions.map((version: any, index: number) => {
                            const isCurrentlyDisplayed = version.id === currentVersionId || 
                                                       (currentVersionId === null && index === 0);
                            
                            return (
                                <div 
                                    key={version.id} 
                                    className={`stats-version-item ${isCurrentlyDisplayed ? 'active' : ''} ${switchingVersion ? 'disabled' : ''}`}
                                    onClick={() => !switchingVersion && switchToVersionAdmin(version.id)}
                                    style={{ cursor: switchingVersion ? 'not-allowed' : 'pointer' }}
                                >
                                    <div className="stats-version-row">
                                        <div className="stats-version-col">
                                            <strong>Version:</strong> {version.version_name}
                                        </div>
                                        <div className="stats-version-col">
                                            <strong>Created:</strong> {new Date(version.created_on).toLocaleString()}
                                        </div>
                                        <div className="stats-version-col">
                                            <div className="stats-version-badges">
                                                {switchingVersion && <span className="stats-loading-tag">Loading...</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="stats-version-actions">
                                        <button 
                                            className="stats-version-preview-btn"
                                            disabled={switchingVersion || isCurrentlyDisplayed}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                switchToVersionAdmin(version.id);
                                            }}
                                        >
                                            {isCurrentlyDisplayed ? 'Currently Viewing' : 'Preview This Version'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Embed Code */}
            <div className="stats-version-card" style={{marginTop: '20px'}}>
                <div className="stats-version-header">
                    <p className="stats-version-title">Embed Code</p>
                </div>
                <div className="stats-version-content">
                    <div className="stats-version-item">
                        <div className="stats-version-row">
                            <div className="stats-version-col">
                                <strong>Script tag:</strong> 
                                <h3>
                                    {'<script src="' + serverUrl + '/api/frontend/get-universal-stats-script/?plan=Trial" defer></script>'}
                                </h3>
                            </div>
                        </div>
                        <div className="stats-version-actions">
                            <button 
                                className='stats-version-preview-btn'
                                onClick={() => copyToClipboard('<script src="' + serverUrl + '/api/frontend/get-universal-stats-script/?plan=Trial" defer></script>', 'script-copy')}
                                style={{
                                    transform: copiedButton === 'script-copy' ? 'scale(1.1)' : 'scale(1)',
                                    backgroundColor: copiedButton === 'script-copy' ? '#007BFF' : '',
                                    transition: 'all 0.1s ease-in-out',
                                }}
                            >
                                {copiedButton === 'script-copy' ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>
                    
                    <div className="stats-version-item">
                        <div className="stats-version-row">
                            <div className="stats-version-col">
                                <strong>Div tag:</strong>
                                <h3>
                                    &lt;div data-stats-id=&quot;{statsData.stats_id}&quot; style=&quot;width: 100%; min-height: 400px; display: block; position: relative; margin: 0; padding: 0; box-sizing: border-box; background: transparent; border: none; outline: none;&quot;&gt;&lt;/div&gt;
                                </h3> 
                            </div>
                        </div>
                        <div className="stats-version-actions">
                            <button 
                                className='stats-version-preview-btn'
                                onClick={() => copyToClipboard(`<div data-stats-id="${statsData.stats_id}" style="width: 100%; min-height: 400px; display: block; position: relative; margin: 0; padding: 0; box-sizing: border-box; background: transparent; border: none; outline: none;"></div>`, 'div-copy')}
                                style={{
                                    transform: copiedButton === 'div-copy' ? 'scale(1.1)' : 'scale(1)',
                                    backgroundColor: copiedButton === 'div-copy' ? '#4CAF50' : '',
                                    color: copiedButton === 'div-copy' ? 'white' : '',
                                    transition: 'all 0.2s ease-in-out',
                                    boxShadow: copiedButton === 'div-copy' ? '0 4px 8px rgba(76, 175, 80, 0.3)' : ''
                                }}
                            >
                                {copiedButton === 'div-copy' ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatsPageView;